/**
 * src/utils/contrasteWcag.ts
 *
 * Chantier J : accessibilité (RGAA 3.2 / WCAG 1.4.3).
 *
 * POURQUOI ce fichier :
 *   La couleur d'accent de la marque (un orange) passe très bien en APLAT
 *   décoratif, mais elle échoue comme COULEUR DE TEXTE : sur blanc elle
 *   plafonne à 3,17:1 alors que le seuil AA du texte est 4,5:1. On ne peut pas
 *   la changer arbitrairement, elle vient de `branding_json` et fait l'identité
 *   de l'événement : on en DÉRIVE une déclinaison assombrie réservée au texte.
 *
 *   L'assombrissement est MULTIPLICATIF sur les trois canaux : le rapport
 *   R:V:B est conservé, donc la teinte et la saturation aussi. On obtient la
 *   même couleur, en plus foncé, pas une autre couleur.
 *
 *   Le calcul vit ici, et NON dans un hex écrit à la main, parce qu'il sert à
 *   DEUX endroits qui doivent donner le même résultat :
 *     - `scripts/build-tokens.ts` (au build, pour la palette compilée) ;
 *     - `components/layout/EventThemeApplier.tsx` (à chaud, quand un autre
 *       événement impose SON accent depuis la base).
 *   Un événement qui change son orange garde donc un texte conforme sans qu'on
 *   ait à recalculer quoi que ce soit à la main.
 *
 *   Aucune dépendance externe : tout vit dans le bundle (hébergement mutualisé,
 *   pas de Node en production, aucun service appelé au runtime).
 *
 * Formule : WCAG 2.x, luminance relative
 *   https://www.w3.org/TR/WCAG21/#dfn-relative-luminance
 *   https://www.w3.org/TR/WCAG21/#dfn-contrast-ratio
 *
 * Mikael - chef de projet - 2026-09-09
 */

/** Seuil AA pour du texte de taille courante (WCAG 1.4.3). */
export const SEUIL_AA_TEXTE = 4.5;

/** Seuil AA pour du texte large et pour les éléments non textuels (1.4.11). */
export const SEUIL_AA_LARGE = 3;

const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

/** Décompose un hex `#RRGGBB` en canaux 0-255. Lève si le format est faux. */
export function hexVersRgb(hex: string): readonly [number, number, number] {
  if (!HEX_RE.test(hex)) {
    throw new Error(`contrasteWcag : hex attendu au format #RRGGBB, reçu « ${hex} ».`);
  }
  const n = hex.slice(1);
  return [
    Number.parseInt(n.slice(0, 2), 16),
    Number.parseInt(n.slice(2, 4), 16),
    Number.parseInt(n.slice(4, 6), 16),
  ];
}

/** Recompose un hex `#RRGGBB` (minuscules) depuis trois canaux 0-255. */
export function rgbVersHex(r: number, v: number, b: number): string {
  const clamp = (x: number): string =>
    Math.max(0, Math.min(255, Math.round(x))).toString(16).padStart(2, '0');
  return `#${clamp(r)}${clamp(v)}${clamp(b)}`;
}

/** Linéarise un canal sRGB 0-255 (étape 1 de la luminance relative WCAG). */
function canalLineaire(canal255: number): number {
  const c = canal255 / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

/** Luminance relative WCAG d'une couleur `#RRGGBB` (0 = noir, 1 = blanc). */
export function luminanceRelative(hex: string): number {
  const [r, v, b] = hexVersRgb(hex);
  return (
    0.2126 * canalLineaire(r) +
    0.7152 * canalLineaire(v) +
    0.0722 * canalLineaire(b)
  );
}

/**
 * Rapport de contraste WCAG entre deux couleurs opaques, dans [1, 21].
 * L'ordre des arguments n'a pas d'importance (la formule est symétrique).
 */
export function rapportDeContraste(a: string, b: string): number {
  const la = luminanceRelative(a);
  const lb = luminanceRelative(b);
  const claire = Math.max(la, lb);
  const sombre = Math.min(la, lb);
  return (claire + 0.05) / (sombre + 0.05);
}

/**
 * Assombrit `couleur` juste ce qu'il faut pour atteindre `ratioCible` sur
 * `fond`, en conservant sa teinte (facteur multiplicatif commun aux trois
 * canaux).
 *
 * Retourne la couleur INCHANGÉE si elle est déjà conforme : on ne touche
 * jamais à une identité qui n'a pas besoin d'être corrigée.
 *
 * Si même le noir ne suffit pas (fond très sombre), on retourne le noir :
 * mieux vaut le meilleur contraste possible qu'une exception qui casse
 * l'écran. Le cas ne se présente pas sur nos fonds clairs.
 */
export function assombrirPourContraste(
  couleur: string,
  fond: string,
  ratioCible: number = SEUIL_AA_TEXTE,
): string {
  if (rapportDeContraste(couleur, fond) >= ratioCible) return couleur;

  const [r0, v0, b0] = hexVersRgb(couleur);
  // Pas de 1 % : ~460 itérations au maximum pour aller jusqu'au noir, donc
  // instantané, et assez fin pour ne jamais assombrir plus que nécessaire.
  for (let facteur = 0.99; facteur > 0; facteur -= 0.01) {
    const candidat = rgbVersHex(r0 * facteur, v0 * facteur, b0 * facteur);
    if (rapportDeContraste(candidat, fond) >= ratioCible) return candidat;
  }
  return '#000000';
}
